import React from 'react';
import cn from 'classnames';
// Hooks
import { useIsProfileUnderReview } from 'hooks';
// Layouts
import ProfileNavigation from 'layouts/ProfileNavigation/ProfileNavigation';
// Components
import TipSuccessModalHandler from 'components/TipSuccessModalHandler/TipSuccessModalHandler';
// Styles
import styles from './Main.module.scss';

type BackgroundColor =
  | 'dark'
  | 'white'
  | 'light-gray'
  | 'cultured'
  | 'transparent'
  | 'background-default'
  | 'background-light'
  | 'background-dim'
  | 'background-main';

type MainProps = {
  className?: string;
  dashboardContentClassName?: string;
  dashboardRootClassName?: string;
  backgroundColor?: BackgroundColor;
  withFixedHeader?: boolean;
  withProfileNavigation?: boolean;
  withoutBottomPadding?: boolean;
  withoutSidesPadding?: boolean;
  showReviewBanner?: boolean; // flag that enables us to skip the review banner - defaults to true, set to false to skip
  withoutPadding?: boolean;
  withHeaderOffset?: boolean; // add margin-top for pages with components without it
  withNavigationWithoutPadding?: boolean; // add margin-top for pages with components without it
  style?: React.CSSProperties; // inline styles (e.g. paddingTop: 0 for stream page)
};

const Main: React.FC<MainProps> = ({
  children,
  className,
  dashboardContentClassName,
  dashboardRootClassName,
  backgroundColor,
  withFixedHeader = true,
  withProfileNavigation,
  withoutBottomPadding,
  withoutSidesPadding,
  showReviewBanner = true,
  withoutPadding = false,
  withHeaderOffset = false,
  withNavigationWithoutPadding = false,
  style,
}) => {
  const isProfileUnderReview = useIsProfileUnderReview();
  const usesProfileNavigation =
    withProfileNavigation || withNavigationWithoutPadding;
  const noBottomPadding = withoutBottomPadding || withoutSidesPadding;
  return (
    <main
      className={cn(
        styles.main,
        {
          [styles[`backgroundColor-${backgroundColor}`]]: backgroundColor,
          [styles.withFixedHeader]: withFixedHeader,
          [styles.withProfileNavigation]: usesProfileNavigation,
          [styles.withoutBottomPadding]: noBottomPadding,
          [styles.withoutSidesPadding]: withoutSidesPadding,
          [styles.withoutPadding]: withoutPadding,
          [styles.withHeaderOffset]: withHeaderOffset,
        },
        className
      )}
      style={style}
    >
      {usesProfileNavigation ? (
        <div
          className={cn(
            styles.dashboardRoot,
            {
              [styles.dashboardRootProfileUnderReview]:
                showReviewBanner && isProfileUnderReview,
            },
            dashboardRootClassName
          )}
        >
          <ProfileNavigation />
          <div
            className={cn(
              styles.dashboardContent,
              {
                [styles.withoutBottomPadding]: withoutBottomPadding,
                [styles.withoutSidesPadding]: withoutSidesPadding,
                [styles.settingsContent]: withNavigationWithoutPadding,
              },
              dashboardContentClassName
            )}
          >
            {children}
          </div>
        </div>
      ) : (
        children
      )}

      <TipSuccessModalHandler />
    </main>
  );
};

export default Main;
